import React, { useState, useEffect, useMemo } from "react";
import { BottomSheet } from "../../../shared/components/BottomSheet.jsx";
import { Button } from "../../../shared/components/Button.jsx";
import { Input } from "../../../shared/components/Input.jsx";
import { useItems } from "../../items/hooks/useItems.js";

export function CustomerPricingModal({ isOpen, onClose, customer, onSavePrices }) {
  const { items, loading: itemsLoading } = useItems();
  const [rates, setRates] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Initialize customer custom rates whenever modal opens or customer changes
  useEffect(() => {
    if (customer && isOpen) {
      setRates(customer.customPrices || {});
      setSaveSuccess(false);
    }
  }, [customer, isOpen]);

  const handleRateChange = (itemId, val) => {
    setRates((prev) => {
      const next = { ...prev };
      if (val === "" || val === null || isNaN(Number(val))) {
        delete next[itemId];
      } else {
        next[itemId] = Number(val);
      }
      return next;
    });
    setSaveSuccess(false);
  };

  const handleClearRate = (itemId) => {
    setRates((prev) => {
      const next = { ...prev };
      delete next[itemId];
      return next;
    });
    setSaveSuccess(false);
  };

  const handleSave = async () => {
    if (!customer?.id) return;
    setSaving(true);
    try {
      // Clean rates to only valid numbers
      const cleanedRates = {};
      for (const [itemId, rate] of Object.entries(rates)) {
        if (typeof rate === "number" && rate >= 0) {
          cleanedRates[itemId] = rate;
        }
      }
      await onSavePrices(customer.id, cleanedRates);
      setSaveSuccess(true);
      setTimeout(() => {
        setSaveSuccess(false);
        onClose();
      }, 800);
    } catch (err) {
      console.error("Failed to save customer prices:", err);
      alert("Failed to save custom rates. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const filteredItems = useMemo(() => {
    if (!items) return [];
    const term = searchTerm.toLowerCase().trim();
    if (!term) return items;
    return items.filter(
      (it) =>
        it.name?.toLowerCase().includes(term) ||
        it.gstCode?.toLowerCase().includes(term) ||
        it.category?.toLowerCase().includes(term)
    );
  }, [items, searchTerm]);

  const customRatesCount = Object.keys(rates).length;

  if (!customer) return null;

  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={onClose}
      title={`Custom Item Rates: ${customer.name}`}
      size="lg"
    >
      <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
        {/* Info header */}
        <div
          style={{
            background: "var(--bg-card)",
            border: "1px solid var(--border-subtle)",
            borderRadius: "var(--radius-md)",
            padding: "0.85rem 1rem",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "0.5rem"
          }}
        >
          <div>
            <div style={{ fontSize: "0.82rem", color: "var(--text-dim)" }}>
              Configuring specific selling prices for this party.
            </div>
            <div style={{ fontSize: "0.75rem", color: "var(--accent-blue)", marginTop: "2px" }}>
              💡 When generating an invoice for {customer.name}, these configured prices will auto-fill by default.
            </div>
          </div>
          <div style={{ textAlign: "right" }}>
            <span
              style={{
                fontSize: "0.75rem",
                fontWeight: "700",
                background: customRatesCount > 0 ? "rgba(59, 130, 246, 0.15)" : "var(--bg-input)",
                color: customRatesCount > 0 ? "var(--primary)" : "var(--text-dim)",
                padding: "3px 8px",
                borderRadius: "999px",
                border: "1px solid var(--border-subtle)"
              }}
            >
              {customRatesCount} custom rate{customRatesCount === 1 ? "" : "s"} active
            </span>
          </div>
        </div>

        {/* Search bar */}
        <div className="search-container" style={{ margin: 0 }}>
          <span className="search-icon">🔍</span>
          <input
            type="text"
            className="search-input"
            placeholder="Search items by name, HSN code, or category..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        {/* Items List */}
        {itemsLoading ? (
          <div className="state-box">
            <div className="state-icon">⏳</div>
            <div className="state-title">Loading Catalog Items...</div>
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="state-box">
            <div className="state-icon">📦</div>
            <div className="state-title">No matching items found</div>
          </div>
        ) : (
          <div
            style={{
              maxHeight: "50vh",
              overflowY: "auto",
              display: "flex",
              flexDirection: "column",
              gap: "0.5rem",
              paddingRight: "4px"
            }}
          >
            {filteredItems.map((item) => {
              const standardPrice = Number(item.sellingPrice || 0);
              const hasCustomRate = rates[item.id] !== undefined;
              const currentCustomVal = hasCustomRate ? rates[item.id] : "";

              return (
                <div
                  key={item.id}
                  style={{
                    background: hasCustomRate ? "rgba(59, 130, 246, 0.05)" : "var(--bg-card)",
                    border: hasCustomRate ? "1px solid var(--primary)" : "1px solid var(--border-subtle)",
                    borderRadius: "var(--radius-md)",
                    padding: "0.75rem 1rem",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: "1rem",
                    transition: "border-color 0.2s"
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: "700", fontSize: "0.95rem", color: "var(--text-main)" }}>
                      {item.name}
                    </div>
                    <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", alignItems: "center", marginTop: "3px" }}>
                      <span style={{ fontSize: "0.75rem", color: "var(--text-dim)" }}>
                        Standard: <strong>₹{standardPrice.toFixed(2)}</strong> / {item.unit || "unit"}
                      </span>
                      {item.gstCode && (
                        <span style={{ fontSize: "0.7rem", color: "var(--text-muted)", background: "var(--bg-input)", padding: "1px 6px", borderRadius: "4px" }}>
                          HSN: {item.gstCode}
                        </span>
                      )}
                    </div>
                  </div>

                  <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                    <div style={{ width: "110px" }}>
                      <input
                        type="number"
                        step="any"
                        placeholder={`₹${standardPrice}`}
                        value={currentCustomVal}
                        onChange={(e) => handleRateChange(item.id, e.target.value)}
                        style={{
                          width: "100%",
                          padding: "0.45rem 0.6rem",
                          borderRadius: "var(--radius-sm)",
                          border: hasCustomRate ? "1px solid var(--primary)" : "1px solid var(--border-subtle)",
                          background: "var(--bg-input)",
                          color: "var(--text-main)",
                          fontSize: "0.9rem",
                          fontWeight: hasCustomRate ? "700" : "normal",
                          textAlign: "right"
                        }}
                      />
                    </div>
                    {hasCustomRate && (
                      <button
                        type="button"
                        onClick={() => handleClearRate(item.id)}
                        title="Reset to standard price"
                        style={{
                          background: "transparent",
                          border: "none",
                          color: "var(--text-dim)",
                          cursor: "pointer",
                          fontSize: "1rem",
                          padding: "4px"
                        }}
                      >
                        ✕
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Footer Actions */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderTop: "1px solid var(--border-subtle)",
            paddingTop: "0.75rem",
            marginTop: "0.5rem"
          }}
        >
          <div style={{ fontSize: "0.8rem", color: saveSuccess ? "var(--success)" : "var(--text-dim)" }}>
            {saveSuccess ? "✓ Rates saved successfully!" : `${customRatesCount} custom rates configured`}
          </div>
          <div style={{ display: "flex", gap: "0.5rem" }}>
            <Button variant="secondary" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSave} disabled={saving}>
              {saving ? "Saving..." : "Save Custom Rates"}
            </Button>
          </div>
        </div>
      </div>
    </BottomSheet>
  );
}

export default CustomerPricingModal;
